import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@material-ui/core";
import type { BoxProps } from "@material-ui/core";
import { AssignmentOutlined as QuestionnaireIcon } from "@material-ui/icons";
import Label, { LabelColor } from "../../Label";
import type { ListingListedFor } from "../../../@types/sharedSchema";
import {
  carriesQuestionnaireStep,
  questionnaireChipStep,
  questionnaireListedForOf,
  signerQuestionnaireStep,
  type QuestionnaireStep,
  type QuestionnaireStepDocument,
  type QuestionnaireStepSigner
} from "../../../utils/signatureQuestionnaireStep";
import { questionnaireLabelContext } from "../../../utils/signatureQuestionnaireName";

export const QUESTIONNAIRE_STEP_COLOR: Record<Exclude<QuestionnaireStep, "none">, LabelColor> = {
  notStarted: "default",
  awaiting: "warning",
  done: "success"
};

/**
 * The questionnaire's chip beside a document's status, on every list that shows documents (the sent
 * list's cards and table, own and office; the client card's חתימות tab; the property card; the
 * document panel): "+ שאלון מוכר" before the contract is signed, "נחתם, ממתין לשאלון" once it is and
 * the questionnaire is still open, "השאלון מולא" once filled while the document is still out. Nothing
 * for a document without the step — a questionnaire on its own included, which has its own labels.
 * The state is `questionnaireStepOf` (utils/signatureQuestionnaireStep.ts), the one rule every screen
 * reads; a rental's questionnaire is "שאלון בעל זכות בנכס".
 */
export default function QuestionnaireStepLabel({ doc, sx }: { doc: QuestionnaireStepDocument; sx?: BoxProps["sx"] }) {
  const { t } = useTranslation();
  const step = questionnaireChipStep(doc);
  if (!step) return null;
  return (
    <Label color={QUESTIONNAIRE_STEP_COLOR[step]} variant="ghost" sx={sx}>
      {t(`signatures.questionnaireStep.chip.${step}`, {
        context: questionnaireLabelContext(questionnaireListedForOf(doc))
      })}
    </Label>
  );
}

/**
 * The same chip for ONE signer, where a document's signers are listed one by one (the client card's
 * expanded row): beside a signer status that still reads "נצפה" after the contract was signed.
 */
export function SignerQuestionnaireStepLabel({ doc, signer }: { doc: QuestionnaireStepDocument; signer: QuestionnaireStepSigner }) {
  const { t } = useTranslation();
  if (!carriesQuestionnaireStep(doc) || signer.status === "declined") return null;
  const step = signerQuestionnaireStep(signer, !!doc.hideAddresses);
  return (
    <Label color={QUESTIONNAIRE_STEP_COLOR[step]} variant="ghost">
      {t(`signatures.questionnaireStep.chip.${step}`, {
        context: questionnaireLabelContext(questionnaireListedForOf(doc))
      })}
    </Label>
  );
}

/**
 * The line under the contract that carries the questionnaire, where several contracts are listed
 * together (the send form's documents, the prepared set dialog): "+ שאלון מוכר, מיד אחרי החתימה על
 * החוזה הזה" — so a seller + exclusivity pair says WHICH of the two brings the questionnaire.
 */
export function QuestionnaireRidesHere({ listedFor, sx }: { listedFor?: ListingListedFor | null; sx?: BoxProps["sx"] }) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={sx} data-testid="questionnaire-rides-here">
      <QuestionnaireIcon sx={{ fontSize: 16, color: "info.main", mt: "2px", flexShrink: 0 }} />
      <Typography variant="caption" sx={{ color: "info.dark", fontWeight: 600, minWidth: 0 }}>
        {t("signatures.questionnaireStep.ridesHere", { context: questionnaireLabelContext(listedFor) })}
      </Typography>
    </Stack>
  );
}
